import { Column, Row, Table } from '@tanstack/react-table';

// Types to support for table cell renderer (e.g. Link, Sparkline, etc.)
export enum CustomCellType {
  LINK = 'Link',
  SPARKLINE = 'Sparkline',
}

export interface CustomCell {
  name: string;
  type?: CustomCellType;
  props?: { [k: string]: unknown };
}

export interface TableColumn {
  field: string;
  cellTemplate?: CustomCell; // json template, advanced use case
  hidden?: boolean;
  width?: string;
  sortBy?: string;
  sortable?: boolean;
  headerName?: string;
  valueType?: ValueType;
  cellRenderer?: CustomCell; // React component
  formatData?: boolean; // whether the data in the column needs to be formatted (data in table could already be formatted)
  dataFormatter?: DataFormatter; // custom formatter to use instead of the default formatter
  align?: ContentAlignment;
}

export interface GridProps {
  table: Table<unknown>;
  tableRows: Row<unknown>[];
  colDefs: TableColumn[];
  onCellClick: (data: { field: string; value: unknown; row: unknown }) => void;
  onRowClick: (data: { row: unknown; field?: string }) => void;
  onScroll: () => void;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  height: number;
  styles?: CustomStyles;
  backgroundColor?: string;
  spacing?: SPACING;
  paddingTop?: number;
  paddingBottom?: number;
  selectedRowIndex?: number;
}

export interface CellRendererProps {
  column: Column<TableColumn>;
  getValue: Function;
  row: Row<TableColumn>;
  table: Table<TableColumn>;
}

export interface ProcessorFunc {
  body: string;
  args?: string[];
}

export interface Attribute {
  attribute: string; // column name
  processorFunc?: string; // jsonata function to process value of attribute
}

export interface Attributes {
  [k: string]: Attribute; // maps variable used in template literal string to corresponding Attribute def
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export interface DataGridProps {
  data: Array<{ [k: string]: unknown }>;
  dataModel?: Model;
  metadata?: { [k: string]: unknown }; // useful for retrieving start and end timestamps and other metadata info
  colDefs?: Array<TableColumn>;
  formatHeader?: boolean;
  onRowClick: () => void;
  onCellClick: () => void;
  fetchNextPage: () => void;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  height: number;
  className?: string;
  styles?: CustomStyles;
  onSortClick?: (data: { orderBy: SortOrder; sortBy: string }) => void;
  spacing?: SPACING;
  sortBy?: string; // used to specify initial sortBy column OR needed when DataGrid is conditionally rendered (e.g. with EmptyBox)
  orderBy?: SortOrder; // used to specify initial orderBy when sorting OR needed when DataGrid is conditionally rendered (e.g. with EmptyBox)

  // adding this as an explicit property to avoid complications with specifying backgroundColor in className
  // takes precedence over background color specified in className
  // (extracting background color from Tailwind className string is not reliable)
  // TODO: revisit how styling is handled in table API
  backgroundColor?: string;
  useRowVirtualization?: boolean;
  selectedRowIndex: number;
}

// Styling
export interface CustomStyleDefinition {
  textColor?: string;
  textColorDark?: string;
  bgColor?: string;
}

export interface CustomStyles {
  grid?: CustomStyleDefinition;
}

export enum CustomStylesMode {
  Light = 'Light',
  Dark = 'Dark',
}

// From UQL
/**
 * Will be autogenerated in UQL-3476
 *
 * SOURCES
 * Internal docs: https://pdx-lev-d01-c0-eks-1-istio-inggw-corp.ops.appd-test.com/uql/documentation/#fetch-specify-observation-fields-to-return
 * Cisco docs: https://developer.cisco.com/docs/appdynamics/query-service/#!fetch/supported-field-types
 * ask-uql Webex: webexteams://im?space=58754140-d0b9-11ed-b416-559c4a5c6b02&message=89b82cb0-4d5d-11ee-bcd1-83eac8b5b5bc
 */
export enum ValueType {
  NUMBER = 'number', // generic number when a more precise numeric type cannot be determined
  LONG = 'long', // integer value
  DOUBLE = 'double', // decimal value

  STRING = 'string', // text or sequence of character
  CSV = 'csv', // string containing potentially multiple logical values separated by a comma character
  BOOLEAN = 'boolean', // true or false

  // global timestamp formatting will be supported in UQL-4590
  TIMESTAMP = 'timestamp', // timestamp in ISO format

  DURATION = 'duration', // ISO 8601 duration

  TIMESERIES = 'timeseries', // complex type with one timestamp field

  JSON = 'json', // JSON object or array
  ANY = 'any', // no specific type
  COMPLEX = 'complex', // nested table, shouldn't be needed for tables
}

export interface Hint {
  kind: string;
  field: string;
  type: string;
}

export interface ModelResultItem {
  alias: string;
  type: string;
  hints: Hint[];
}

export interface ComplexModelResultItem extends ModelResultItem {
  form: string;
  model: Model;
}

export interface Model {
  name: string;
  fields: ModelResultItem[] | ComplexModelResultItem[];
  type: 'complex' | 'model';
}

// Styling
export enum ContentAlignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

// https://magnetic.cisco.com/0a43ab5cd/p/313a90-table/t/69634e
export enum SPACING {
  COMPACT = 'compact', // default
  COMFY = 'comfy',
  SPACIOUS = 'spacious',
}

// https://magnetic.cisco.com/0a43ab5cd/p/313a90-table/t/69634e
export const PADDING = {
  compact: 8,
  comfy: 12,
  spacious: 16,
};

// Formatting
export enum FormatterType {
  UIK = 'uik',
}

export interface DataFormatter {
  type: FormatterType;
}

export type UikFormatter = DataFormatter & {
  dataType: string; // e.g. "number"
  formatType: string; // e.g. "short"
  options?: Record<string, any>; // e.g. { "maximumFractionDigits": 2 }
};
